import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Popover,
  Typography,
  useTheme,
} from "@mui/material";
import DataTable from "../../components/DataTable";
import { motion } from "framer-motion";
import { LogsResponse, Logs } from "../../types/api.types";
import { logService } from "../../services/logs/logService";
import FilterLogs, { FilterLogsFormData } from "../../components/FilterLogs";
import FilterAltIcon from "@mui/icons-material/FilterAlt";

const LogPage = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [currentPage, setCurrentPage] = useState(0);

  const [logs, setLogs] = useState<LogsResponse | null>(null);

  const [searchFilters, setSearchFilters] = useState<FilterLogsFormData | null>(
    null,
  );

  const theme = useTheme();

  const columns = [
    {
      header: "Id",
      key: "id",
    },
    {
      header: "Mensaje",
      key: "message",
    },
    {
      header: "Nivel",
      key: "level",
      render: (value: Logs) => {
        let color, bg, border;

        switch (value.level) {
          case "Information":
            color = theme.palette.success.main;
            bg = theme.palette.success.main + "20";
            border = theme.palette.success.main + "40";
            break;
          case "Warning":
            color = theme.palette.warning.main;
            bg = theme.palette.warning.main + "20";
            border = theme.palette.warning.main + "40";
            break;
          case "Error":
          default:
            color = theme.palette.error.main;
            bg = theme.palette.error.main + "20";
            border = theme.palette.error.main + "40";
            break;
        }

        return (
          <Chip
            label={value.level}
            size="small"
            sx={{
              backgroundColor: bg,
              color: color,
              fontWeight: "600",
              border: `1px solid ${border}`,
              borderRadius: 2,
            }}
          />
        );
      },
    },
    {
      header: "Fecha",
      key: "TimeStampLocal",
      render: (value: Logs) => {
        const date = new Date(value?.timeStampLocal);
        const day = date.getDate().toString().padStart(2, "0");
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, "0");
        const minutes = date.getMinutes().toString().padStart(2, "0");

        return `${day}/${month}/${year} ${hours}.${minutes}`;
      },
    },
  ];

  const getProducts = async () => {
    setIsLoading(true);
    const response = await logService.getLogs(currentPage, 10, searchFilters);
    setLogs(response);
    setIsLoading(false);
  };

  useEffect(() => {
    getProducts();
  }, [currentPage, searchFilters]);

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null,
  );

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const hasFilters = useMemo(() => searchFilters !== null, [searchFilters]);

  return (
    <Box>
      <Card>
        <CardContent>
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            style={{
              display: "flex",
              alignItems: "center",
              marginBottom: 10,
            }}
          >
            <Typography variant="h4">Logs</Typography>
            {hasFilters && (
              <Button
                onClick={() => setSearchFilters(null)}
                sx={{ ml: 2 }}
                variant="outlined"
                color="primary"
              >
                Limpiar filtros
              </Button>
            )}

            <Button
              onClick={handleClick}
              aria-describedby={id}
              sx={{ p: 0, m: 0, width: 0 }}
            >
              <FilterAltIcon sx={{ color: "primary.main" }} />
            </Button>
          </motion.div>

          <Popover
            id={id}
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
          >
            <Box sx={{ p: 2 }}>
              <FilterLogs
                searchFilters={searchFilters}
                onCancel={handleClose}
                onFilter={(values: FilterLogsFormData) => {
                  setSearchFilters(values);
                  handleClose();
                }}
              />
            </Box>
          </Popover>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 2 }}
            style={{ marginTop: 2 }}
          >
            <DataTable
              isLoading={isLoading}
              columns={columns}
              total={logs?.total || 0}
              data={logs?.logs || []}
              page={currentPage + 1}
              onChange={(page) => {
                setCurrentPage(page - 1);
              }}
            />
          </motion.div>
        </CardContent>
      </Card>
    </Box>
  );
};

export default LogPage;
